/**
 * Share-URL builder + share() helper.
 *
 * Social bots (WhatsApp, FB, Twitter, iMessage, Instagram) DO NOT execute JS,
 * so the canonical SPA URLs (`/blog/<slug>`, `/<store-slug>`, `/franchise`)
 * cannot return per-page meta tags through React/Helmet alone.
 *
 * Solution: backend exposes pre-rendered Open Graph pages at
 *   /api/og/blog/<slug>
 *   /api/og/store/<slug>
 *   /api/og/franchise
 *   /api/og/blog
 * which serve correct meta tags + JS redirect to the SPA route.
 *
 * In-app Share buttons must use these URLs — that's how WhatsApp/Twitter/etc.
 * get rich link previews.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Normalize backend origin so share URLs always use the public origin (petfolk.in)
// in production, even when called from the preview environment.
function publicShareOrigin() {
  // If we're on petfolk.in (or another non-preview origin), use that.
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host && !host.includes('preview.emergentagent.com')) {
      return `${window.location.protocol}//${host}`;
    }
  }
  return BACKEND_URL;
}

export function getShareUrl(type, slug) {
  const origin = publicShareOrigin();
  if (type === 'blog' && slug) return `${origin}/api/og/blog/${encodeURIComponent(slug)}`;
  if (type === 'blogIndex') return `${origin}/api/og/blog`;
  if (type === 'store' && slug) return `${origin}/api/og/store/${encodeURIComponent(slug)}`;
  if (type === 'franchise') return `${origin}/api/og/franchise`;
  return origin;
}

/** Trigger Web Share API if available, else copy to clipboard. Returns the method used. */
export async function sharePage({ title, text, type, slug }) {
  const url = getShareUrl(type, slug);
  if (typeof navigator !== 'undefined' && navigator.share) {
    try {
      await navigator.share({ title, text, url });
      return 'web_share';
    } catch (e) {
      if (e?.name === 'AbortError') return 'cancelled';
      // fall through to clipboard
    }
  }
  try {
    await navigator.clipboard.writeText(url);
    return 'clipboard';
  } catch {
    // last resort: prompt
    if (typeof window !== 'undefined') window.prompt('Copy this link', url);
    return 'manual';
  }
}
